import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import axios from 'axios';
import { Button } from '../ui/button';
import { Card, CardContent } from '../ui/card';
import { Badge } from '../ui/badge';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Checkbox } from '../ui/checkbox';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from '../ui/dialog';
import {
  LayoutDashboard, Gamepad2, Calendar, Users, MapPin, Trophy,
  UserCircle, ClipboardList, CalendarRange, Shield, Settings,
  Loader2, ExternalLink, Check, Trash2, AlertTriangle, Plus,
  ArrowLeft, RefreshCw, FlaskConical, Globe,
  UsersRound, UserCog, ClipboardCheck, Image, ArrowUp, ArrowDown, Database
} from 'lucide-react';
import DataTable from './DataTable';
import EntityForm from './EntityForm';
import { ResetCacheButton } from '../ResetCacheButton';
import ScheduleBuilder from './ScheduleBuilder';
import ConfirmDialog from './ConfirmDialog';
import MediaLibraryPanel from './MediaLibraryPanel';
import OlImporter from './OlImporter';
import SyncHealthPill from './SyncHealthPill';
import { ENTITIES, ENTITY_NAV_ORDER } from './entityConfigs';
import bridgeApi from './bridgeApi';
import { emitEditSaved } from './wpSyncEvents';
import { toast } from 'sonner';
import { useAuth } from '../../contexts/AuthContext';

// Entity keys that map 1:1 to a WP sync projection key and therefore
// support the scoped per-row "Pull this from WP" action
// (POST /api/wp-sync/{org}/pull/{entity}/{wp_id}). Kept in lockstep with
// services/wp_projection_registry.py REGISTRY keys.
const PULLABLE_ENTITIES = new Set([
  'games', 'teams', 'venues', 'schedules', 'sports', 'players', 'coaches', 'positions',
]);

// Resolve a row's WP post id across the per-entity id conventions the
// canonical adapter emits (games expose `wp_id`; teams/venues surface it
// as a numeric `id`). Returns null when the row isn't synced to WP yet.
function wpIdOf(row) {
  if (row?.wp_id != null && /^\d+$/.test(String(row.wp_id))) return Number(row.wp_id);
  if (row?.id != null && /^\d+$/.test(String(row.id))) return Number(row.id);
  return null;
}

const API = process.env.REACT_APP_BACKEND_URL;

const ICONS = {
  LayoutDashboard, Gamepad2, Calendar, Users, MapPin, Trophy,
  UserCircle, ClipboardList, CalendarRange, Shield, Settings,
  UsersRound, UserCog, ClipboardCheck, Image,
};

// ═══════════════════════════════════════════════════════════
// CONNECTION PANEL (with multi-site selector + add-site dialog)
// ═══════════════════════════════════════════════════════════
function detectIsProduction(url) {
  try {
    const host = new URL(url).hostname.toLowerCase();
    // 2 segments = apex (foo.com); 3+ = subdomain (sws.foo.com)
    return host.split('.').length === 2;
  } catch { return false; }
}

function AddSiteDialog({ open, orgId, onClose, onAdded }) {
  const [siteUrl, setSiteUrl] = useState('');
  const [apiKey, setApiKey] = useState('');
  const [label, setLabel] = useState('');
  const [isProduction, setIsProduction] = useState(false);
  const [productionTouched, setProductionTouched] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);

  // Auto-detect production from URL whenever URL changes (unless user manually overrode)
  useEffect(() => {
    if (!productionTouched) setIsProduction(detectIsProduction(siteUrl));
  }, [siteUrl, productionTouched]);

  const reset = () => {
    setSiteUrl(''); setApiKey(''); setLabel('');
    setIsProduction(false); setProductionTouched(false);
    setSaving(false); setError(null);
  };

  const handleSave = async (activate) => {
    if (!siteUrl || !apiKey) { setError('Site URL and API key are required'); return; }
    setSaving(true); setError(null);
    try {
      const body = { site_url: siteUrl.trim(), api_key: apiKey.trim(), label: label.trim() || null, is_production: isProduction };
      if (activate) {
        const res = await bridgeApi.saveConfig(orgId, body);
        if (!res.ok || !res.connected) {
          setError(res.error?.message || 'Connection failed — check URL and key');
          setSaving(false);
          return;
        }
      } else {
        await bridgeApi.addKnownSite(orgId, body);
      }
      reset(); onAdded?.(activate); onClose?.();
    } catch (e) {
      setError(e.response?.data?.detail || e.message);
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => { if (!o && !saving) { reset(); onClose?.(); } }}>
      <DialogContent className="bg-slate-900 border-slate-700 text-slate-100 max-w-md" data-testid="add-site-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Globe className="h-4 w-4 text-amber-500" /> Add WordPress Site
          </DialogTitle>
          <DialogDescription className="text-slate-400 text-xs">
            Register a new bridge endpoint. You can save it for later or connect immediately.
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-3 py-2">
          <div className="space-y-1">
            <Label className="text-slate-200 text-xs">WordPress Site URL</Label>
            <Input
              value={siteUrl}
              onChange={e => setSiteUrl(e.target.value)}
              placeholder="https://your-site.com"
              className="bg-slate-800 border-slate-700 text-slate-100 placeholder:text-slate-500"
              data-testid="add-site-url"
            />
          </div>
          <div className="space-y-1">
            <Label className="text-slate-200 text-xs">OL Secret Key</Label>
            <Input
              value={apiKey}
              onChange={e => setApiKey(e.target.value)}
              placeholder="X-OL-Key value from WP"
              type="password"
              className="bg-slate-800 border-slate-700 text-slate-100 placeholder:text-slate-500"
              data-testid="add-site-key"
            />
          </div>
          <div className="space-y-1">
            <Label className="text-slate-200 text-xs">Label (optional)</Label>
            <Input
              value={label}
              onChange={e => setLabel(e.target.value)}
              placeholder="e.g. Production, Staging, Alex's Sandbox"
              className="bg-slate-800 border-slate-700 text-slate-100 placeholder:text-slate-500"
              data-testid="add-site-label"
            />
          </div>
          <label className="flex items-start gap-2 cursor-pointer pt-1" data-testid="add-site-prod-toggle">
            <Checkbox
              checked={isProduction}
              onCheckedChange={(v) => { setIsProduction(!!v); setProductionTouched(true); }}
              className="mt-0.5 border-slate-500 data-[state=checked]:bg-red-600 data-[state=checked]:border-red-600"
            />
            <span className="text-xs text-slate-200 leading-tight">
              This is the <strong className="text-red-400">production</strong> site
              <span className="block text-[10px] text-slate-500 mt-0.5">
                Auto-detected from URL: {detectIsProduction(siteUrl) ? 'production' : 'test/staging'}. You can override.
              </span>
            </span>
          </label>
          {error && (
            <div className="text-xs text-red-400 bg-red-900/20 p-2 rounded flex items-start gap-1.5">
              <AlertTriangle className="h-3 w-3 mt-0.5 shrink-0" />{error}
            </div>
          )}
        </div>
        <DialogFooter className="gap-2 sm:gap-2">
          <Button
            variant="outline" disabled={saving}
            onClick={() => { reset(); onClose?.(); }}
            className="bg-slate-800 border-slate-600 text-slate-200 hover:bg-slate-700"
          >Cancel</Button>
          <Button
            variant="outline" disabled={saving || !siteUrl || !apiKey}
            onClick={() => handleSave(false)}
            className="bg-slate-800 border-slate-600 text-slate-200 hover:bg-slate-700"
            data-testid="add-site-save"
          >Save (don't connect)</Button>
          <Button
            disabled={saving || !siteUrl || !apiKey}
            onClick={() => handleSave(true)}
            className="bg-amber-600 hover:bg-amber-700 text-white"
            data-testid="add-site-save-and-connect"
          >
            {saving ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <ExternalLink className="h-4 w-4 mr-2" />}
            Save & Connect
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function ConnectionPanel({ orgId, siteConfig, setSiteConfig, onConnected, onDeleteRequest }) {
  const [siteUrl, setSiteUrl] = useState(siteConfig?.site_url || '');
  const [apiKey, setApiKey] = useState('');
  const [saving, setSaving] = useState(false);
  const [testing, setTesting] = useState(false);
  const [switching, setSwitching] = useState(null); // site_url being switched to
  const [error, setError] = useState(null);
  const [knownSites, setKnownSites] = useState([]);
  const [showAddDialog, setShowAddDialog] = useState(false);

  const reloadKnown = useCallback(async () => {
    try {
      const res = await bridgeApi.listKnownSites(orgId);
      if (res.ok) setKnownSites(res.data || []);
    } catch { /* non-fatal */ }
  }, [orgId]);

  useEffect(() => { reloadKnown(); }, [reloadKnown, siteConfig?.site_url]);

  const pollRelinkAndToast = async (startedAt, siteLabel) => {
    const host = (siteLabel || '').replace(/^https?:\/\//, '');
    toast.info(`Re-linking data to ${host} in the background…`);
    for (let i = 0; i < 15; i++) {
      await new Promise(r => setTimeout(r, 2000));
      try {
        const { last_relink } = await bridgeApi.relinkStatus(orgId);
        if (last_relink?.at && Date.parse(last_relink.at) >= startedAt - 2000) {
          if (last_relink.ok === false) {
            toast.warning(`Re-link to ${host} hit an issue — sync will retry automatically.`);
            return;
          }
          const t = last_relink.totals || {};
          const bits = [];
          if (t.adopted) bits.push(`${t.adopted} linked`);
          if (t.seeded) bits.push(`${t.seeded} imported`);
          if (t.applied) bits.push(`${t.applied} updated`);
          toast.success(`Re-linked to ${host} — ${bits.length ? bits.join(', ') : 'already in sync'}`);
          return;
        }
      } catch { /* keep polling */ }
    }
  };

  const handleConnect = async () => {
    if (!siteUrl || !apiKey) return;
    const startedAt = Date.now();
    setSaving(true); setError(null);
    try {
      const data = await bridgeApi.saveConfig(orgId, {
        site_url: siteUrl, api_key: apiKey,
        is_production: detectIsProduction(siteUrl),
      });
      if (data.ok && data.connected) {
        setSiteConfig(data.data); onConnected?.();
        if (data.relink_scheduled) pollRelinkAndToast(startedAt, siteUrl);
      }
      else setError(data.error?.message || 'Connection failed');
    } catch (e) { setError(e.response?.data?.detail || e.message); }
    setSaving(false);
  };

  const handleTest = async () => {
    setTesting(true); setError(null);
    try {
      const data = await bridgeApi.testConfig(orgId);
      if (data.ok) setSiteConfig(prev => ({ ...prev, status: 'connected', health_data: data.data }));
      else setError(data.error?.message || 'Test failed');
    } catch (e) { setError(e.message); }
    setTesting(false);
  };

  const handleSwitch = async (targetUrl) => {
    if (!targetUrl || targetUrl === siteConfig?.site_url) return;
    const startedAt = Date.now();
    setSwitching(targetUrl); setError(null);
    try {
      const data = await bridgeApi.activateKnownSite(orgId, targetUrl);
      if (data.ok && data.connected) {
        setSiteConfig(data.data);
        onConnected?.();
        if (data.relink_scheduled) pollRelinkAndToast(startedAt, targetUrl);
      } else {
        setError(data.error?.message || 'Switch failed');
      }
    } catch (e) {
      setError(e.response?.data?.detail || e.message);
    }
    setSwitching(null);
  };

  // Disconnect — uses the project-wide ConfirmDialog
  const handleDisconnect = () => {
    onDeleteRequest?.({
      title: 'Disconnect this WordPress site?',
      description: `This will disconnect ${siteConfig?.site_name || siteConfig?.site_url || 'the current site'} from OmniLeagues. Your saved sites list and WP data are not affected — you can reconnect anytime.`,
      confirmLabel: 'Yes, disconnect',
      destructive: true,
      onConfirm: async () => {
        await bridgeApi.deleteConfig(orgId);
        setSiteConfig(null);
        await reloadKnown();
      },
    });
  };

  // Forget a saved site (and disconnect if active)
  const handleForget = (site) => {
    onDeleteRequest?.({
      title: `Forget ${site.label || site.site_url}?`,
      description: site.is_active
        ? 'This is the active site. Forgetting it will also disconnect WP Mgmt. WP data is not affected.'
        : 'This removes the saved entry from the registry. WP data is not affected.',
      confirmLabel: 'Yes, forget',
      destructive: true,
      onConfirm: async () => {
        await bridgeApi.removeKnownSite(orgId, site.site_url);
        if (site.is_active) setSiteConfig(null);
        await reloadKnown();
      },
    });
  };

  const connected = siteConfig?.status === 'connected';
  const health = siteConfig?.health_data;

  if (connected && health) {
    return (
      <>
        <Card className="bg-emerald-900/20 border-emerald-700/50" data-testid="connection-panel-connected">
          <CardContent className="p-4">
            <div className="flex items-center justify-between mb-3 flex-wrap gap-2">
              <div className="flex items-center gap-2 flex-wrap">
                <div className="h-2.5 w-2.5 rounded-full bg-emerald-500 animate-pulse" />
                <span className="font-semibold text-emerald-300 text-sm">{health.site_name}</span>
                <span className="text-xs text-slate-500">Bridge v{health.bridge_version}</span>
                {siteConfig?.is_production ? (
                  <Badge className="bg-red-500/20 text-red-300 border-red-500/30 text-[10px] uppercase">Production</Badge>
                ) : (
                  <Badge className="bg-amber-500/20 text-amber-300 border-amber-500/40 text-[10px] uppercase">Test / Staging</Badge>
                )}
              </div>
              <Badge variant="outline" className="bg-emerald-500/10 text-emerald-400 border-emerald-500/30 text-[10px]">Connected</Badge>
            </div>
            <div className="text-[11px] text-slate-400 mb-2 truncate">{siteConfig.site_url}</div>
            <div className="grid grid-cols-4 gap-2 mb-3">
              {[{ l: 'Sports', v: health.sports_configured }, { l: 'Teams', v: health.teams_count }, { l: 'Players', v: health.players_count }, { l: 'Games', v: health.games_count }].map((s, i) => (
                <div key={i} className="text-center p-1.5 bg-slate-800/50 rounded">
                  <p className="text-base font-bold text-slate-100">{s.v}</p>
                  <p className="text-[9px] text-slate-400 uppercase">{s.l}</p>
                </div>
              ))}
            </div>
            <div className="text-[10px] text-slate-500">OL Secret Key: {siteConfig.api_key_preview} | WP {health.wp_version} | PHP {health.php_version}</div>

            {/* Switch site dropdown — a value change here switches
                immediately. We render the URL itself as the primary
                label (AJ rightly flagged that "Test [TEST]" is opaque
                — operators want the actual hostname they're pointing
                at, not a friendly nickname they don't remember
                creating). */}
            <div className="flex flex-wrap items-center gap-2 mt-3">
              <Label className="text-[10px] text-slate-400 uppercase tracking-wider">Switch site:</Label>
              <select
                className="h-7 text-xs rounded-md px-2 bg-slate-800 border border-slate-600 text-slate-100 min-w-[260px]"
                value={siteConfig.site_url || ''}
                disabled={!!switching}
                onChange={e => handleSwitch(e.target.value)}
                data-testid="site-switch-dropdown"
                title="Pick a saved site to switch immediately. PROD = production site, TEST = test/staging."
              >
                {knownSites.map(s => (
                  <option key={s.site_url} value={s.site_url}>
                    {s.site_url}{s.is_production ? '  • PROD' : '  • TEST'}
                  </option>
                ))}
                {/* Ensure current is in the list even if not yet in known_sites */}
                {!knownSites.some(s => s.site_url === siteConfig.site_url) && (
                  <option value={siteConfig.site_url}>{siteConfig.site_url}</option>
                )}
              </select>
              <Button
                variant="outline" size="sm"
                onClick={() => setShowAddDialog(true)}
                className="border-amber-700/60 text-amber-300 hover:bg-amber-900/30 h-7 text-[11px]"
                data-testid="add-site-btn"
              >
                <Plus className="h-3 w-3 mr-1" /> Add Site
              </Button>
              {switching && <Loader2 className="h-3.5 w-3.5 animate-spin text-amber-400" />}
            </div>

            {/* Saved sites list — URL primary, no friendly-name redundancy */}
            {knownSites.length > 0 && (
              <div className="mt-3 rounded border border-slate-700/60 divide-y divide-slate-700/50">
                {knownSites.map(s => (
                  <div key={s.site_url} className="flex items-center justify-between gap-2 px-2 py-1.5 text-[11px]" data-testid={`known-site-${s.site_url}`}>
                    <div className="flex items-center gap-2 min-w-0 flex-1">
                      {s.is_active
                        ? <Check className="h-3 w-3 text-emerald-400 shrink-0" />
                        : <span className="h-3 w-3 shrink-0" />}
                      <span className="text-slate-200 font-mono truncate">{s.site_url}</span>
                      {s.is_production
                        ? <Badge className="bg-red-500/20 text-red-300 border-red-500/30 text-[9px] uppercase">Prod</Badge>
                        : <Badge className="bg-amber-500/20 text-amber-300 border-amber-500/30 text-[9px] uppercase">Test</Badge>}
                      {s.is_active && (
                        <Badge className="bg-emerald-500/20 text-emerald-300 border-emerald-500/30 text-[9px] uppercase">Active</Badge>
                      )}
                    </div>
                    <div className="flex items-center gap-1 shrink-0">
                      {/* Per-row Switch button removed — switching now happens
                          via the dropdown above with a single selection. The
                          delete (forget) action stays here. */}
                      <Button variant="ghost" size="icon" className="h-6 w-6 text-slate-500 hover:text-red-400"
                        onClick={() => handleForget(s)}
                        data-testid={`forget-${s.site_url}`}
                        title="Forget this site (removes the saved entry)."
                      ><Trash2 className="h-3 w-3" /></Button>
                    </div>
                  </div>
                ))}
              </div>
            )}

            {error && (
              <div className="mt-2 text-xs text-red-400 bg-red-900/20 p-2 rounded flex items-start gap-1.5">
                <AlertTriangle className="h-3 w-3 mt-0.5 shrink-0" />{error}
              </div>
            )}

            <div className="flex gap-2 mt-3">
              <Button variant="outline" size="sm" onClick={handleTest} disabled={testing} className="border-slate-600 text-slate-300 h-7 text-xs" data-testid="test-connection-btn">
                {testing ? <Loader2 className="h-3 w-3 animate-spin mr-1" /> : <RefreshCw className="h-3 w-3 mr-1" />} Test
              </Button>
              <Button variant="outline" size="sm" onClick={handleDisconnect} className="border-red-800/50 text-red-400/70 hover:bg-red-900/30 h-7 text-xs" data-testid="disconnect-btn">
                <Trash2 className="h-3 w-3 mr-1" /> Disconnect
              </Button>
            </div>
          </CardContent>
        </Card>
        <AddSiteDialog
          open={showAddDialog} orgId={orgId}
          onClose={() => setShowAddDialog(false)}
          onAdded={async (activated) => {
            await reloadKnown();
            if (activated) {
              const cfg = await bridgeApi.getConfig(orgId);
              if (cfg.ok && cfg.data) setSiteConfig(cfg.data);
              onConnected?.();
            }
          }}
        />
      </>
    );
  }

  return (
    <>
      <div className="max-w-lg mx-auto py-8" data-testid="connection-panel-setup">
        <div className="text-center mb-6">
          <Calendar className="h-10 w-10 mx-auto text-amber-500 mb-2" />
          <h2 className="text-lg font-bold text-slate-100">Connect WordPress Site</h2>
          <p className="text-xs text-slate-300 mt-1">Enter your site URL and OL Secret Key to manage schedules, games, and rosters.</p>
        </div>
        <Card className="bg-slate-800/60 border-slate-700">
          <CardContent className="p-5 space-y-4">
            <div className="space-y-1.5">
              <Label className="text-slate-200 text-xs">WordPress Site URL</Label>
              <Input value={siteUrl} onChange={e => setSiteUrl(e.target.value)} placeholder="https://your-site.com" className="bg-slate-900 border-slate-700 text-slate-200" data-testid="site-url-input" />
            </div>
            <div className="space-y-1.5">
              <Label className="text-slate-200 text-xs">OL Secret Key</Label>
              <Input value={apiKey} onChange={e => setApiKey(e.target.value)} placeholder="From WP Settings > OmniLeagues" type="password" className="bg-slate-900 border-slate-700 text-slate-200" data-testid="api-key-input" />
              <p className="text-[10px] text-slate-500 leading-relaxed">The shared secret OL and WordPress use to authenticate each other. Must match the value at <span className="text-amber-400 font-medium">WP Admin → Settings → OmniLeagues → OL Secret Key</span>.</p>
            </div>
            {error && <div className="text-xs text-red-400 bg-red-900/20 p-2 rounded flex items-center gap-1"><AlertTriangle className="h-3 w-3" />{error}</div>}
            <div className="flex gap-2">
              <Button onClick={handleConnect} disabled={!siteUrl || !apiKey || saving} className="flex-1 bg-amber-600 hover:bg-amber-700" data-testid="connect-btn">
                {saving ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <ExternalLink className="h-4 w-4 mr-2" />}Connect
              </Button>
              {knownSites.length > 0 && (
                <select
                  className="h-9 text-xs rounded-md px-2 bg-slate-900 border border-slate-700 text-slate-100 max-w-[40%]"
                  defaultValue=""
                  onChange={e => { if (e.target.value) handleSwitch(e.target.value); }}
                  data-testid="site-switch-dropdown-disconnected"
                >
                  <option value="">Use saved site…</option>
                  {knownSites.map(s => (
                    <option key={s.site_url} value={s.site_url}>
                      {s.site_url}{s.is_production ? '  • PROD' : '  • TEST'}
                    </option>
                  ))}
                </select>
              )}
            </div>
            <Button
              variant="outline"
              onClick={() => setShowAddDialog(true)}
              className="w-full border-amber-700/60 text-amber-300 hover:bg-amber-900/30"
              data-testid="add-site-btn-setup"
            >
              <Plus className="h-3 w-3 mr-1" /> Add to Known Sites
            </Button>
          </CardContent>
        </Card>
      </div>
      <AddSiteDialog
        open={showAddDialog} orgId={orgId}
        onClose={() => setShowAddDialog(false)}
        onAdded={async (activated) => {
          await reloadKnown();
          if (activated) {
            const cfg = await bridgeApi.getConfig(orgId);
            if (cfg.ok && cfg.data) setSiteConfig(cfg.data);
            onConnected?.();
          }
        }}
      />
    </>
  );
}

// ═══════════════════════════════════════════════════════════
// PRODUCTION / TEST BANNER
// ═══════════════════════════════════════════════════════════
function SiteEnvBanner({ siteConfig }) {
  if (!siteConfig || siteConfig.status !== 'connected') return null;
  const isProd = !!siteConfig.is_production;
  if (isProd) return null; // No banner on production — silence is golden
  return (
    <div
      className="flex items-center gap-2 px-3 py-2 rounded-md bg-amber-500/15 border border-amber-500/40 text-amber-200 text-xs"
      data-testid="site-env-banner"
    >
      <FlaskConical className="h-4 w-4 shrink-0" />
      <div className="flex-1 leading-tight">
        <strong className="text-amber-100">Test / staging environment.</strong>{' '}
        Connected to <code className="text-amber-100 font-mono">{siteConfig.site_url}</code>
        {siteConfig.label ? <span className="opacity-80"> — {siteConfig.label}</span> : null}.
        Edits here will <em>not</em> affect production.
      </div>
    </div>
  );
}

// ═══════════════════════════════════════════════════════════
// ADMIN DASHBOARD
// ═══════════════════════════════════════════════════════════
function AdminDashboard({ health, siteConfig, onNavigate, orgId, role }) {
  // Local counts state — sourced directly from the same ol-canonical
  // list endpoints the WP Mgmt grids use, so the dashboard tiles match
  // what the user clicks into. The WP `/health` response surfaced
  // mismatched totals (Sports said 6 when OL had 3) because WP counts
  // tagged terms differently than OL Mongo does. Single source of
  // truth wins.
  const [counts, setCounts] = useState({
    games: null, teams: null, players: null, sports: null,
    schedules: null, venues: null, coaches: null,
    positions: null, 'staff-groups': null,
  });

  useEffect(() => {
    if (!orgId) return;
    let cancelled = false;
    const entities = Object.keys(counts);
    Promise.all(entities.map(ent =>
      bridgeApi.get(`/${ent}`, orgId, { limit: 1 })
        .then(d => [ent, d?.meta?.total ?? 0])
        .catch(() => [ent, null]),
    )).then(pairs => {
      if (cancelled) return;
      const next = Object.fromEntries(pairs);
      setCounts(next);
    });
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [orgId]);

  if (!health) return null;
  const tiles = [
    { key: 'games',    label: 'Games',    value: counts.games,    icon: 'Gamepad2' },
    { key: 'teams',    label: 'Teams',    value: counts.teams,    icon: 'Users' },
    { key: 'players',  label: 'Players',  value: counts.players,  icon: 'UserCircle' },
    { key: 'sports',   label: 'Sports',   value: counts.sports,   icon: 'Trophy' },
  ];

  return (
    <div className="space-y-4" data-testid="admin-dashboard">
      <div>
        <h2 className="text-lg font-bold text-slate-100">{health.site_name}</h2>
        <p className="text-xs text-slate-400">{siteConfig?.site_url} | Bridge v{health.bridge_version}</p>
        <p className="text-[10px] text-slate-500 mt-0.5">Counts below are live from the OmniLeagues canonical store — same numbers the WP Mgmt grids show.</p>
      </div>
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        {tiles.map(c => {
          const Icon = ICONS[c.icon];
          const display = c.value === null ? '—' : c.value.toLocaleString();
          return (
            <Card key={c.key} className="bg-slate-800/50 border-slate-700 hover:border-amber-600/40 transition-colors cursor-pointer" onClick={() => onNavigate(c.key)} data-testid={`dash-${c.key}`}>
              <CardContent className="p-4 text-center">
                {Icon && <Icon className="h-5 w-5 mx-auto text-amber-500 mb-1" />}
                <p className="text-2xl font-bold text-slate-100" data-testid={`dash-${c.key}-value`}>{display}</p>
                <p className="text-[10px] text-slate-400 uppercase tracking-wider">{c.label}</p>
              </CardContent>
            </Card>
          );
        })}
      </div>
      {/* Secondary tiles for the staff workflow + supporting entities. */}
      <div className="grid grid-cols-3 md:grid-cols-6 gap-2">
        {[
          { key: 'schedules',     label: 'Schedules' },
          { key: 'venues',        label: 'Venues' },
          { key: 'coaches',       label: 'Staff' },
          { key: 'positions',     label: 'Staff Positions' },
          { key: 'staff-groups',  label: 'Staff Groups' },
        ].map(c => (
          <Card key={c.key} className="bg-slate-800/30 border-slate-700/40 hover:border-amber-600/30 cursor-pointer" onClick={() => onNavigate(c.key)} data-testid={`dash-${c.key}`}>
            <CardContent className="p-2 text-center">
              <p className="text-base font-semibold text-slate-100" data-testid={`dash-${c.key}-value`}>{counts[c.key] === null ? '—' : counts[c.key]?.toLocaleString() ?? 0}</p>
              <p className="text-[10px] text-slate-400 uppercase tracking-wider truncate">{c.label}</p>
            </CardContent>
          </Card>
        ))}
      </div>
      <Card className="bg-slate-800/30 border-slate-700/50">
        <CardContent className="p-4">
          <h3 className="text-xs font-bold text-slate-200 uppercase mb-2">Quick Actions</h3>
          <div className="flex flex-wrap gap-2">
            <Button size="sm" className="bg-amber-600 hover:bg-amber-700 h-8 text-xs" onClick={() => onNavigate('games', 'new')} data-testid="quick-new-game"><Plus className="h-3 w-3 mr-1" />New Game</Button>
            {role !== 'tech_specialist' && (
              <Button size="sm" className="bg-cyan-700 hover:bg-cyan-800 h-8 text-xs" onClick={() => onNavigate('games', 'builder')} data-testid="quick-schedule-builder"><Calendar className="h-3 w-3 mr-1" />Schedule Builder</Button>
            )}
            <Button size="sm" variant="outline" className="border-slate-600 text-slate-300 h-8 text-xs" onClick={() => onNavigate('teams')} data-testid="quick-teams">Manage Teams</Button>
            <Button size="sm" variant="outline" className="border-slate-600 text-slate-300 h-8 text-xs" onClick={() => onNavigate('schedules')} data-testid="quick-schedules">Schedules</Button>
            <Button size="sm" variant="outline" className="border-slate-600 text-slate-300 h-8 text-xs" onClick={() => onNavigate('players')} data-testid="quick-players">Players</Button>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}

// ═══════════════════════════════════════════════════════════
// MAIN ADMIN MANAGER
// ═══════════════════════════════════════════════════════════
export default function AdminManager({ orgId }) {
  const { user } = useAuth();
  const primaryRole = user?.roles?.[0]?.role_type || '';
  // Site connection
  const [siteConfig, setSiteConfig] = useState(null);
  const [configLoading, setConfigLoading] = useState(true);
  const isLive = siteConfig?.status === 'connected';

  // Navigation
  const [activeEntity, setActiveEntity] = useState('dashboard');
  const [viewMode, setViewMode] = useState('list'); // list | edit | new | builder
  const [selectedItem, setSelectedItem] = useState(null);
  const [showImport, setShowImport] = useState(false);

  // Entity data cache
  const [entityData, setEntityData] = useState({});
  const [entityMeta, setEntityMeta] = useState({});
  const [entityLoading, setEntityLoading] = useState({});

  // Search & pagination
  const [searchTerms, setSearchTerms] = useState({});
  const [pages, setPages] = useState({});
  // v5.2.3 — Inline filters for the Games tab (season + team)
  const [gamesFilter, setGamesFilter] = useState({ season: '', team: 'all', sport: 'all', hideEvents: true, show: 'all' });
  // Aug-2026 — inline filters for the newly-unioned tabs so every tab that
  // spans multiple seasons is filterable to the season/sport the user wants.
  // season:'' is the "not yet defaulted" sentinel — the default-to-current-season
  // effects below set it to the current season on first load (never fights a
  // deliberate "All seasons" pick, which is the literal value 'all').
  const [schedulesFilter, setSchedulesFilter] = useState({ season: '' });
  // games_publish_flag_mode (migration_log_0169): ON reveals the Games grid
  // 'Website' (W) column + the Show filter. OFF (default) = grid unchanged.
  const [publishFlagOn, setPublishFlagOn] = useState(false);
  // migration_log_0174 — open the Games tab on the org's configured default
  // sport. gamesSportTouchedRef flips true the moment the user picks a sport,
  // so the default never fights a deliberate choice.
  const gamesSportTouchedRef = useRef(false);
  useEffect(() => {
    if (!orgId) return;
    axios.get(`${API}/api/ol-canonical/publish-flag-mode/${orgId}`, { withCredentials: true })
      .then(r => setPublishFlagOn((r.data?.games_publish_flag_mode) === 'on'))
      .catch(() => {});
  }, [orgId]);
  useEffect(() => {
    if (!orgId) return;
    axios.get(`${API}/api/ol-canonical/wp-mgmt-defaults/${orgId}`, { withCredentials: true })
      .then(r => {
        const def = r.data?.wp_mgmt_default_sport || '';
        if (def && !gamesSportTouchedRef.current) {
          // Only override the initial 'all' sentinel; the current-season effect
          // then re-picks the season for this sport.
          setGamesFilter(p => (p.sport === 'all' ? { ...p, sport: def, season: '' } : p));
        }
      })
      .catch(() => {});
  }, [orgId]);
  // Default = current + next season; past seasons stay one click away (rosters are never deleted).
  const [rostersFilter, setRostersFilter] = useState({ season: 'current' });
  const [playersFilter, setPlayersFilter] = useState({ sport: 'all', season: '', roster: 'all' });
  // Staff (coaches) filters — sport + team(group) + season.
  const [staffFilter, setStaffFilter] = useState({ sport: 'all', team: 'all', season: '' });
  // Staff Positions filter — sport (or "org" for leadership/board/steering roles) + level.
  const [positionsFilter, setPositionsFilter] = useState({ sport: 'all', level: 'all' });
  // Staff Positions — Level filter options built DYNAMICALLY from the levels
  // that actually appear in the positions dataset (all rows load client-side,
  // so this is the true distinct set). No hardcoded level list; unknown codes
  // fall through as an upper-cased label.
  const positionLevelOptions = useMemo(() => {
    const LABELS = { vb: 'Varsity Boys', vg: 'Varsity Girls', vc: 'Varsity Coed',
      jvb: 'JV Boys', jvbw: 'JV Boys White', jvg: 'JV Girls',
      msb: 'MS Boys', msg: 'MS Girls', msbd: 'MS Boys Dev', msw: 'MS Boys White' };
    const ORDER = ['vb', 'vg', 'vc', 'jvb', 'jvbw', 'jvg', 'msb', 'msg', 'msbd', 'msw'];
    const present = new Set();
    for (const r of (entityData.positions || [])) {
      const lv = (r.level || '').trim().toLowerCase();
      if (lv) present.add(lv);
    }
    const known = ORDER.filter(lv => present.has(lv));
    const extra = [...present].filter(lv => !ORDER.includes(lv)).sort();
    return [...known, ...extra].map(lv => ({ value: lv, label: LABELS[lv] || lv.toUpperCase() }));
  }, [entityData.positions]);
  // Staff Groups filter — Level, built dynamically from the `level_gender`
  // values present in the staff-groups dataset (all rows load client-side).
  const [staffGroupsFilter, setStaffGroupsFilter] = useState({ level: 'all' });
  const staffGroupLevelOptions = useMemo(() => {
    const LABELS = { vb: 'Varsity Boys', vg: 'Varsity Girls', vc: 'Varsity Coed',
      jvb: 'JV Boys', jvbw: 'JV Boys White', jvg: 'JV Girls',
      msb: 'MS Boys', msg: 'MS Girls', msbd: 'MS Boys Dev', msw: 'MS Boys White' };
    const ORDER = ['vb', 'vg', 'vc', 'jvb', 'jvbw', 'jvg', 'msb', 'msg', 'msbd', 'msw'];
    const present = new Set();
    for (const r of (entityData['staff-groups'] || [])) {
      const lv = (r.level_gender || '').trim().toLowerCase();
      if (lv) present.add(lv);
    }
    const known = ORDER.filter(lv => present.has(lv));
    const extra = [...present].filter(lv => !ORDER.includes(lv)).sort();
    return [...known, ...extra].map(lv => ({ value: lv, label: LABELS[lv] || lv.toUpperCase() }));
  }, [entityData]);
  // Shared, sport-aware season option cache. ONE source + ONE fetch shape for
  // every tab that shows a season dropdown, keyed by sport ('all' | 'vl' |
  // 'bk' | 'bb'). The dropdown for a sport-scoped tab shows only the seasons
  // that sport actually has data for (e.g. volleyball → 2026-2027 only).
  const [seasonsBySport, setSeasonsBySport] = useState({});
  // SWS-internal teams (limited list, ~13 rows) — used by the Games tab's
  // "All our teams" filter dropdown. Distinct from `entityData.teams`,
  // which is paginated to 200 rows alphabetically over 271 teams and so
  // misses most SWS-internal teams.
  const [swsTeams, setSwsTeams] = useState([]);
  // De-duplicate the Games team-filter dropdown by display label. With
  // sport="All sports" the same label appears once per sport (e.g. "Varsity
  // Boys" for baseball `bb-vb` AND basketball `varsity-boys`); the backend
  // filter resolves either id to the same level, so collapsing to one option
  // per label removes the confusing duplicates without changing results.
  const dedupedSwsTeams = useMemo(() => {
    const seen = new Set();
    const out = [];
    for (const t of swsTeams) {
      const label = (t.title || t.name || t.slug || '').trim().toLowerCase();
      if (!label || seen.has(label)) continue;
      seen.add(label);
      out.push(t);
    }
    return out;
  }, [swsTeams]);
  // Complete, de-duplicated Teams + Venues lists for the Games edit-form
  // pickers (Opponent + Game Location). `entityData.teams/venues` is the
  // 200-row paginated grid data, which truncated those dropdowns alphabetically
  // (~535 teams stopped at "G"/Goldsboro; 325 venues). These load the full set
  // once so every opponent/venue is selectable.
  const [pickerTeams, setPickerTeams] = useState([]);
  const [pickerVenues, setPickerVenues] = useState([]);
  // Per-entity sort state: { entityKey: { key, dir: 'asc'|'desc' } }.
  // Applied client-side (the backend already returns alphabetically
  // sorted rows by the entity's title field — this layer just lets the
  // user re-sort the visible page without a round-trip).
  const [sortConfigs, setSortConfigs] = useState({});

  // Saving
  const [saving, setSaving] = useState(false);
  const [conflict, setConflict] = useState(null); // {local, remote} for 409 resolution

  // Universal confirm-dialog state — used for ALL destructive actions per project rule.
  const [confirmState, setConfirmState] = useState(null);

  // Load site config
  useEffect(() => {
    if (!orgId) return;
    (async () => {
      try {
        const data = await bridgeApi.getConfig(orgId);
        if (data.ok && data.data) setSiteConfig(data.data);
      } catch { /* no config */ }
      setConfigLoading(false);
    })();
  }, [orgId]);

  const fetchEntity = useCallback(async (entityKey, opts = {}) => {
    if (!isLive || !ENTITIES[entityKey]?.apiPath) return;
    // ``silent`` skips the loading-spinner state. Used by the inline
    // cell-save flow so the table doesn't blink on every keystroke
    // commit; the optimistic patch already shows the new value, and
    // the silent refetch just lets server-side normalisations
    // (auto-slug, computed_result, modified_at) flow back in.
    if (!opts.silent) setEntityLoading(prev => ({ ...prev, [entityKey]: true }));
    try {
      const cfg = ENTITIES[entityKey];
      // Games span every season (~2.8k rows); use a larger page so operators
      // see far more than the newest 200 at once. Other entities keep 200.
      const params = { limit: entityKey === 'games' ? 1000 : 200 };
      if (opts.page) params.page = opts.page;
      // Server-side search — so audit queries hit ALL rows, not just the
      // currently-loaded page. (Otherwise a "noah" search over 1256 players
      // only filters the first 200 loaded and misses everyone past slot 200.)
      const q = (opts.q !== undefined ? opts.q : searchTerms[entityKey]) || '';
      if (q.trim()) params.q = q.trim();
      // Games-specific inline filters (season + team). The backend joins
      // through `db.schedules` so games returned for "2026-2027" are
      // genuinely scoped to that season — not a client-side filter over
      // the 200 loaded rows.
      if (entityKey === 'games') {
        const seasonVal = opts.season !== undefined ? opts.season : gamesFilter.season;
        const teamVal = opts.team !== undefined ? opts.team : gamesFilter.team;
        const sportVal = opts.sport !== undefined ? opts.sport : gamesFilter.sport;
        if (seasonVal && seasonVal !== 'all') params.season = seasonVal;
        if (teamVal && teamVal !== 'all') params.team = teamVal;
        if (sportVal && sportVal !== 'all') params.sport = sportVal;
        const hideEv = opts.hideEvents !== undefined ? opts.hideEvents : gamesFilter.hideEvents;
        if (hideEv) params.hide_events = 1;
        const showVal = opts.show !== undefined ? opts.show : gamesFilter.show;
        if (showVal && showVal !== 'all') params.show = showVal;
      }
      if (entityKey === 'schedules') {
        const v = opts.season !== undefined ? opts.season : schedulesFilter.season;
        if (v && v !== 'all') params.season = v;
      }
      if (entityKey === 'rosters') {
        const v = opts.season !== undefined ? opts.season : rostersFilter.season;
        if (v && v !== 'all') params.season = v;
      }
      if (entityKey === 'players') {
        const sp = opts.sport !== undefined ? opts.sport : playersFilter.sport;
        const se = opts.season !== undefined ? opts.season : playersFilter.season;
        const ro = opts.roster !== undefined ? opts.roster : playersFilter.roster;
        if (sp && sp !== 'all') params.sport = sp;
        if (se && se !== 'all') params.season = se;
        if (ro && ro !== 'all') params.roster = ro;
      }
      if (entityKey === 'coaches') {
        const sp = opts.sport !== undefined ? opts.sport : staffFilter.sport;
        const se = opts.season !== undefined ? opts.season : staffFilter.season;
        const tm = opts.team !== undefined ? opts.team : staffFilter.team;
        if (sp && sp !== 'all') params.sport = sp;
        if (se && se !== 'all') params.season = se;
        if (tm && tm !== 'all') params.team = tm;
      }
      if (entityKey === 'positions') {
        const sp = opts.sport !== undefined ? opts.sport : positionsFilter.sport;
        if (sp && sp !== 'all') params.sport = sp;
      }
      const data = await bridgeApi.get(cfg.apiPath, orgId, params);
      const items = Array.isArray(data?.data) ? data.data : [];
      setEntityData(prev => ({ ...prev, [entityKey]: items }));
      setEntityMeta(prev => ({ ...prev, [entityKey]: data?.meta || {} }));
    } catch (e) {
      console.error(`Fetch ${entityKey}:`, e);
      setEntityData(prev => ({ ...prev, [entityKey]: prev[entityKey] || [] }));
    }
    if (!opts.silent) setEntityLoading(prev => ({ ...prev, [entityKey]: false }));
  }, [orgId, isLive, searchTerms, gamesFilter, schedulesFilter, rostersFilter, playersFilter, staffFilter, positionsFilter]);

  // Shared sport-aware season loader — every season dropdown uses this one
  // fetch shape (`/seasons?sport=`) and reads from the same cache, so the
  // Games / Players / Staff / Schedules / Rosters filters are guaranteed to
  // agree on which seasons exist for a given sport.
  const loadSeasons = useCallback(async (sport = 'all') => {
    const key = sport || 'all';
    try {
      const params = key !== 'all' ? { sport: key, limit: 60 } : { limit: 60 };
      const d = await bridgeApi.get('/seasons', orgId, params);
      const list = Array.isArray(d?.data) ? d.data : [];
      setSeasonsBySport(prev => ({ ...prev, [key]: list }));
    } catch { /* keep prior */ }
  }, [orgId]);

  // Season options for a tab given its current sport context. Falls back to
  // the all-sports list while a sport-scoped list is still loading.
  const seasonOptionsFor = useCallback((sport = 'all') => {
    const key = sport || 'all';
    return seasonsBySport[key] || seasonsBySport.all || entityData.seasons || [];
  }, [seasonsBySport, entityData.seasons]);

  // Debounce server-side search: 350ms after the user stops typing,
  // re-fetch the active entity with the search term applied. Resets to
  // page 1 because the prior page index makes no sense in a new result set.
  useEffect(() => {
    if (!isLive || activeEntity === 'dashboard') return undefined;
    const term = searchTerms[activeEntity] || '';
    const t = setTimeout(() => {
      setPages(prev => ({ ...prev, [activeEntity]: 1 }));
      fetchEntity(activeEntity, { page: 1, q: term });
    }, 350);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchTerms[activeEntity], activeEntity, isLive]);

  // Re-fetch games when the Games-tab inline filters (season/team) change.
  // Resets to page 1 — old page index is meaningless against a new filtered set.
  useEffect(() => {
    if (!isLive || activeEntity !== 'games') return;
    // Re-load the SWS team dropdown each time the sport filter flips so
    // the list always shows only teams for the active sport. We let the
    // backend do the sport→sport_id resolution (`?sport=<slug>`) instead
    // of filtering on the client — the sports adapter returns the WP id
    // in `id` but teams store sport_id as the OL UUID, so client-side
    // matching against `sportDoc.id` silently misses everything.
    const params = { limit: 100, sws_only: 1 };
    if (gamesFilter.sport && gamesFilter.sport !== 'all') {
      params.sport = gamesFilter.sport;
    }
    bridgeApi.get('/teams', orgId, params)
      .then(d => setSwsTeams(Array.isArray(d?.data) ? d.data : []))
      .catch(() => setSwsTeams([]));
    setPages(prev => ({ ...prev, games: 1 }));
    fetchEntity('games', {
      page: 1,
      season: gamesFilter.season,
      team: gamesFilter.team,
      sport: gamesFilter.sport,
      hideEvents: gamesFilter.hideEvents,
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [gamesFilter.season, gamesFilter.team, gamesFilter.sport, gamesFilter.hideEvents, activeEntity, isLive]);

  // Reset the team filter whenever the selected sport changes — a Volleyball
  // team is no longer a valid filter once the user picks Baseball, and
  // letting a stale id linger turns the games grid empty with no obvious cause.
  useEffect(() => {
    if (gamesFilter.team === 'all') return;
    setGamesFilter(p => ({ ...p, team: 'all' }));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [gamesFilter.sport]);

  // Refetch Schedules / Rosters / Players when their inline filters change.
  useEffect(() => {
    if (!isLive || activeEntity !== 'schedules') return;
    setPages(prev => ({ ...prev, schedules: 1 }));
    fetchEntity('schedules', { page: 1, season: schedulesFilter.season });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [schedulesFilter.season, activeEntity, isLive]);

  useEffect(() => {
    if (!isLive || activeEntity !== 'rosters') return;
    setPages(prev => ({ ...prev, rosters: 1 }));
    fetchEntity('rosters', { page: 1, season: rostersFilter.season });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [rostersFilter.season, activeEntity, isLive]);

  useEffect(() => {
    if (!isLive || activeEntity !== 'players') return;
    setPages(prev => ({ ...prev, players: 1 }));
    fetchEntity('players', { page: 1, sport: playersFilter.sport, season: playersFilter.season, roster: playersFilter.roster });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [playersFilter.sport, playersFilter.season, playersFilter.roster, activeEntity, isLive]);

  // Staff (coaches) refetch on filter change.
  useEffect(() => {
    if (!isLive || activeEntity !== 'coaches') return;
    setPages(prev => ({ ...prev, coaches: 1 }));
    fetchEntity('coaches', { page: 1, sport: staffFilter.sport, season: staffFilter.season, team: staffFilter.team });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [staffFilter.sport, staffFilter.season, staffFilter.team, activeEntity, isLive]);

  // Staff Positions refetch on sport filter change.
  useEffect(() => {
    if (!isLive || activeEntity !== 'positions') return;
    setPages(prev => ({ ...prev, positions: 1 }));
    fetchEntity('positions', { page: 1, sport: positionsFilter.sport });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [positionsFilter.sport, activeEntity, isLive]);

  // Load the all-sports season list once, then a sport-scoped list whenever a
  // sport-aware tab changes its sport. Reset the season back to "all" when the
  // sport changes so a now-invalid season (e.g. bk-2018 under volleyball)
  // doesn't linger and empty the grid.
  useEffect(() => { if (isLive) loadSeasons('all'); }, [isLive, loadSeasons]);
  useEffect(() => {
    loadSeasons(gamesFilter.sport);
    // Sport changed → the prior season may not exist for the new sport. Clear it
    // so the "default to current season" effect below re-picks the right one.
    setGamesFilter(p => (p.season === '' ? p : { ...p, season: '' }));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [gamesFilter.sport]);
  // Default the Games season filter to the CURRENT season (never "all"). Uses
  // the STRICT sport-scoped list (not the all-sports fallback) so it waits for
  // the right list to load and doesn't stick a foreign sport's current season.
  useEffect(() => {
    if (!isLive || activeEntity !== 'games') return;
    const key = gamesFilter.sport || 'all';
    const list = seasonsBySport[key];
    if (!list || !list.length) return; // wait for this sport's season list
    if (gamesFilter.season && list.some(s => s.slug === gamesFilter.season)) return;
    const current = list.find(s => s.is_current) || list.find(s => (s.count || 0) > 0) || list[0];
    if (current && current.slug !== gamesFilter.season) {
      setGamesFilter(p => ({ ...p, season: current.slug }));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [seasonsBySport, gamesFilter.sport, gamesFilter.season, activeEntity, isLive]);
  useEffect(() => {
    loadSeasons(playersFilter.sport);
    // Sport changed → clear so the "default to current season" effect re-picks,
    // and reset the roster (a roster of the old sport is no longer valid).
    setPlayersFilter(p => (p.season === '' && p.roster === 'all' ? p : { ...p, season: '', roster: 'all' }));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [playersFilter.sport]);
  useEffect(() => {
    loadSeasons(staffFilter.sport);
    setStaffFilter(p => (p.season === '' ? p : { ...p, season: '' }));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [staffFilter.sport]);

  // Default the SCHEDULES / PLAYERS / STAFF season filters to the CURRENT
  // season on first load (mirrors the Games tab). Only auto-picks while the
  // filter is still the '' sentinel, so a deliberate "All seasons" pick sticks.
  const _pickCurrent = useCallback((list) =>
    (list.find(s => s.is_current) || list.find(s => (s.count || 0) > 0) || list[0]), []);
  useEffect(() => {
    if (!isLive || activeEntity !== 'schedules' || schedulesFilter.season !== '') return;
    const list = seasonsBySport.all || entityData.seasons;
    if (!list || !list.length) return;
    const cur = _pickCurrent(list);
    if (cur) setSchedulesFilter({ season: cur.slug });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [seasonsBySport, entityData.seasons, schedulesFilter.season, activeEntity, isLive]);
  useEffect(() => {
    if (!isLive || activeEntity !== 'players' || playersFilter.season !== '') return;
    const list = seasonsBySport[playersFilter.sport || 'all'];
    if (!list || !list.length) return;
    const cur = _pickCurrent(list);
    if (cur) setPlayersFilter(p => ({ ...p, season: cur.slug }));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [seasonsBySport, playersFilter.sport, playersFilter.season, activeEntity, isLive]);
  useEffect(() => {
    if (!isLive || activeEntity !== 'coaches' || staffFilter.season !== '') return;
    const list = seasonsBySport[staffFilter.sport || 'all'];
    if (!list || !list.length) return;
    const cur = _pickCurrent(list);
    if (cur) setStaffFilter(p => ({ ...p, season: cur.slug }));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [seasonsBySport, staffFilter.sport, staffFilter.season, activeEntity, isLive]);

  // Fetch active entity when tab changes
  useEffect(() => {
    if (isLive && activeEntity !== 'dashboard') {
      fetchEntity(activeEntity, { page: pages[activeEntity] || 1 });
    }
  }, [activeEntity, isLive, fetchEntity, pages]);

  // Pre-fetch related entities for pickers
  useEffect(() => {
    if (!isLive) return;
    ['sports', 'teams', 'venues', 'schedules', 'seasons', 'leagues', 'staff-groups', 'rosters'].forEach(k => {
      if (!entityData[k]) fetchEntity(k);
    });
  }, [isLive, fetchEntity, entityData]);

  // Load the COMPLETE teams + venues lists for the entity-picker dropdowns
  // (Opponent, Game Location). Fetched once with a high limit so the Games
  // edit form shows every opponent/venue — not just the first 200 rows.
  useEffect(() => {
    if (!isLive || !orgId) return;
    bridgeApi.get('/teams', orgId, { limit: 1000 })
      .then(d => setPickerTeams(Array.isArray(d?.data) ? d.data : []))
      .catch(() => {});
    bridgeApi.get('/venues', orgId, { limit: 1000 })
      .then(d => setPickerVenues(Array.isArray(d?.data) ? d.data : []))
      .catch(() => {});
  }, [isLive, orgId]);

  const navigateTo = (entity, mode = 'list', item = null) => {
    setActiveEntity(entity);
    setViewMode(mode);
    setSelectedItem(item);
  };

  const handleSave = async (values) => {
    const cfg = ENTITIES[activeEntity];
    if (!cfg?.apiPath) return;
    setSaving(true);
    try {
      // Strip internal fields not meant for WP
      const payload = { ...values };
      delete payload._siteTimezone;
      // Players modal writes team via the Roster Team picker (`roster_slug`).
      // The display shadows ride along in the row → strip them so they never
      // trigger the inline current_team_id reassignment (which would clobber a
      // two-sport player's other-sport membership). Drop an unchanged/empty
      // roster_slug so a plain save doesn't attempt a move.
      if (activeEntity === 'players') {
        delete payload.current_team_id;
        delete payload.current_team_label;
        if (!payload.roster_slug) delete payload.roster_slug;
      }

      // Split off OL-only metadata — these are saved to /api/team-meta, NOT WP
      const olMeta = payload._ol_meta;
      delete payload._ol_meta;

      let savedId = payload.id;
      if (payload.id) {
        if (payload._ol_revision_id !== undefined) payload.revision_id = payload._ol_revision_id;
        await bridgeApi.patch(cfg.apiPath, orgId, payload.id, payload);
      } else {
        const res = await bridgeApi.post(cfg.apiPath, orgId, payload);
        savedId = res?.data?.id || res?.id || savedId;
      }

      // Persist OL meta if the entity supports it (currently: teams)
      if (activeEntity === 'teams' && olMeta && savedId) {
        try {
          await axios.put(`${API}/api/team-meta/${orgId}/${savedId}`, {
            opponent_type: olMeta.opponent_type || '',
            is_conference: !!olMeta.is_conference,
            notes: olMeta.notes || '',
          });
        } catch (metaErr) {
          console.warn('OL team-meta save failed:', metaErr);
        }
      }

      await fetchEntity(activeEntity);
      // Modal save → outbox push to WP. Notify the pill / banner.
      emitEditSaved({ entity: activeEntity, rowId: savedId, source: 'modal' });
      setViewMode('list');
      setSelectedItem(null);
    } catch (e) {
      if (e.response?.status === 409) {
        setConflict({ local: values, remote: e.response?.data?.data?.current });
      } else {
        console.error('Save error:', e);
        alert(`Error: ${e.response?.data?.detail || e.message}`);
      }
    }
    setSaving(false);
  };

  // Inline-edit cell save. Called from DataTable when the user blurs a
  // cell after changing its value. Issues a one-field PATCH to the
  // bridge, optimistically updates local state, and re-fetches the row
  // to surface any server-side normalisation (slug suffixing, computed
  // fields, etc.).
  //
  // Errors are RE-THROWN — DataTable's wrapper records them in its
  // own errorMap so the row's bottom error bar can list them all.
  const handleCellSave = useCallback(async (row, colKey, newValue) => {
    const cfg = ENTITIES[activeEntity];
    if (!cfg?.apiPath || !row?.id) return;
    const patch = { [colKey]: newValue };
    // Player jersey edits must target the team_memberships entry (the field
    // the WP push reads) — send the row's current team slug so the backend
    // routes it to the right membership instead of the inert top-level field.
    if (activeEntity === 'players' && (colKey === 'number' || colKey === 'jersey_number') && row.current_team_slug) {
      patch.team_slug = row.current_team_slug;
    }
    // Optimistic local update so the cell shows the new value
    // immediately even before the round-trip lands. Server response
    // overwrites this on success; on failure DataTable surfaces the
    // error and the optimistic value persists in the local state until
    // the user re-edits or refetches.
    setEntityData(prev => {
      const list = prev[activeEntity] || [];
      const next = list.map(r => (r.id === row.id ? { ...r, ...patch } : r));
      return { ...prev, [activeEntity]: next };
    });
    // Forward the row's revision id so the optimistic-concurrency
    // guard in bridgeApi can reject stale writes (same as the modal).
    if (row._ol_revision_id !== undefined) patch.revision_id = row._ol_revision_id;
    await bridgeApi.patch(cfg.apiPath, orgId, row.id, patch);
    // Tell the SyncHealthPill (and any banner) that a write just
    // crossed the OL→WP outbox so they can flip amber immediately
    // instead of waiting for the next 30s health poll.
    emitEditSaved({ entity: activeEntity, rowId: row.id, field: colKey });
    // Refetch — quietly — so any server-derived fields (auto-slug,
    // computed_result, modified_at) flow back into the grid.
    try { await fetchEntity(activeEntity, { silent: true }); } catch { /* ignore */ }
  }, [activeEntity, orgId, fetchEntity]);

  // Generic header-action runner — used by entity-level buttons like "Auto-classify Teams"
  const handleHeaderAction = (action) => {
    setConfirmState({
      title: action.confirmTitle || action.label,
      description: action.confirmDescription || `Run ${action.label}?`,
      confirmLabel: `Yes, run`,
      destructive: false,
      onConfirm: async () => {
        try {
          const url = `${API}${action.endpoint.replace('{org_id}', orgId)}`;
          const res = await axios.post(url);
          await fetchEntity(activeEntity);
          const summary = res?.data?.data;
          if (summary) {
            const by = summary.by_type ? Object.entries(summary.by_type).map(([k, v]) => `${k}=${v}`).join(', ') : '';
            alert(`${action.label} done.\nUpdated: ${summary.updated}\nSkipped (already classified): ${summary.skipped_user_set}\n${by}`);
          }
        } catch (e) {
          alert(`Action failed: ${e.response?.data?.detail || e.message}`);
        }
      },
    });
  };

  const handleBulkSave = useCallback(async (patches) => {
    // patches: [{ row, fields: { colKey: value, ... } }]
    // One PATCH per row (merging all queued field edits), parallelised
    // at 5 concurrent so a burst of 50 cell edits flushes in ~10s without
    // hammering the backend. Each successful PATCH emits a sync event so
    // the pill flips amber → green for the entire burst.
    const cfg = ENTITIES[activeEntity];
    if (!cfg?.apiPath || !patches?.length) return;
    const queue = patches.slice();
    const failures = [];
    const worker = async () => {
      while (queue.length) {
        const { row, fields } = queue.shift();
        // Player jersey edits carry the team slug so the backend writes to the
        // correct team_memberships entry (the field that pushes to WP).
        if (activeEntity === 'players' && ('number' in fields || 'jersey_number' in fields) && row.current_team_slug) {
          fields.team_slug = row.current_team_slug;
        }
        try {
          await bridgeApi.patch(cfg.apiPath, orgId, row.id, fields);
        } catch (e) {
          failures.push({ row, err: e });
        }
      }
    };
    await Promise.all(Array.from({ length: Math.min(5, patches.length) }, worker));
    emitEditSaved({ entity: activeEntity, count: patches.length - failures.length, source: 'bulk_save' });
    await fetchEntity(activeEntity);
    if (failures.length) {
      const head = failures.slice(0, 3).map(f =>
        `· ${f.row.title || f.row.name || '#' + f.row.id}: ${f.err?.response?.data?.detail || f.err?.message}`).join('\n');
      const more = failures.length > 3 ? `\n…and ${failures.length - 3} more` : '';
      throw new Error(`${failures.length} of ${patches.length} save(s) failed:\n${head}${more}`);
    }
  }, [activeEntity, orgId, fetchEntity]);

  const handleBulkDelete = useCallback(async (ids) => {
    // ids[] are row.ol_id values (DataTable's keying convention).
    // We resolve back to row.id (WP/Mongo id used by the DELETE route)
    // so the same single-row endpoint handles each. Concurrency capped
    // at 5 to be a polite client of WP + outbox without serialising
    // 200 deletes one-by-one.
    const cfg = ENTITIES[activeEntity];
    if (!cfg?.apiPath) return;
    const idSet = new Set(ids);
    const rows = (entityData[activeEntity] || []).filter(r => idSet.has(r.ol_id || r.id));
    if (rows.length === 0) return;
    const failures = [];
    const queue = [...rows];
    const worker = async () => {
      while (queue.length) {
        const row = queue.shift();
        try {
          await bridgeApi.del(cfg.apiPath, orgId, row.id);
        } catch (e) {
          failures.push({ row, err: e?.response?.data?.detail || e?.message });
        }
      }
    };
    await Promise.all(Array.from({ length: Math.min(5, rows.length) }, worker));
    await fetchEntity(activeEntity);
    // Each successful delete enqueues an outbox push. Surface to pill.
    emitEditSaved({ entity: activeEntity, count: rows.length - failures.length, source: 'bulk_delete' });
    if (failures.length) {
      const head = failures.slice(0, 3).map(f =>
        `· ${f.row.title || f.row.name || f.row.slug || '#' + f.row.id}: ${f.err}`).join('\n');
      const more = failures.length > 3 ? `\n…and ${failures.length - 3} more` : '';
      throw new Error(`${failures.length} of ${rows.length} delete(s) failed:\n${head}${more}`);
    }
  }, [activeEntity, entityData, orgId, fetchEntity]);

  // Scoped per-row pull — imports only THIS row's latest values from WP
  // via the guarded scoped endpoint (no bulk seeding). Refetches the grid
  // + nudges the sync pill so the operator sees the change land.
  const handleRowPull = useCallback(async (row) => {
    const wpId = wpIdOf(row);
    if (!wpId) {
      toast.error('Not synced to WordPress yet — nothing to pull for this row.');
      return;
    }
    try {
      const { data } = await axios.post(
        `${API}/api/wp-sync/${orgId}/pull/${activeEntity}/${wpId}`,
        null, { withCredentials: true },
      );
      const parts = [];
      if (data?.applied) parts.push(`${data.applied} updated`);
      if (data?.adopted) parts.push(`${data.adopted} linked`);
      if (data?.seeded) parts.push(`${data.seeded} imported`);
      if (data?.unchanged) parts.push(`${data.unchanged} unchanged`);
      toast.success(parts.length ? `Pulled from WP — ${parts.join(', ')}.` : 'Already up to date with WordPress.');
      await fetchEntity(activeEntity, { silent: true });
      emitEditSaved({ entity: activeEntity, rowId: row.id, source: 'scoped_pull' });
    } catch (e) {
      toast.error(`Pull failed: ${e.response?.data?.detail || e.message}`);
    }
  }, [orgId, activeEntity, fetchEntity]);

  // Scoped bulk pull — refresh several selected rows from WP in one action.
  const handleBulkPull = useCallback(async (rows) => {
    let ok = 0, skip = 0, fail = 0;
    const totals = { applied: 0, adopted: 0, seeded: 0, unchanged: 0 };
    for (const row of rows) {
      const wpId = wpIdOf(row);
      if (!wpId) { skip++; continue; }
      try {
        const { data } = await axios.post(
          `${API}/api/wp-sync/${orgId}/pull/${activeEntity}/${wpId}`,
          null, { withCredentials: true },
        );
        ok++;
        ['applied', 'adopted', 'seeded', 'unchanged'].forEach(k => { totals[k] += data?.[k] || 0; });
      } catch (e) { fail++; }
    }
    const parts = [];
    if (totals.applied) parts.push(`${totals.applied} updated`);
    if (totals.adopted) parts.push(`${totals.adopted} linked`);
    if (totals.seeded) parts.push(`${totals.seeded} imported`);
    if (totals.unchanged) parts.push(`${totals.unchanged} unchanged`);
    const tail = `${skip ? `; ${skip} skipped (not synced)` : ''}${fail ? `; ${fail} failed` : ''}`;
    toast[fail ? 'warning' : 'success'](`Pulled ${ok} row${ok === 1 ? '' : 's'} from WP${parts.length ? ` — ${parts.join(', ')}` : ''}${tail}`);
    await fetchEntity(activeEntity, { silent: true });
    if (ok) emitEditSaved({ entity: activeEntity, count: ok, source: 'bulk_pull' });
  }, [orgId, activeEntity, fetchEntity]);

  const handleDelete = (item) => {
    const cfg = ENTITIES[activeEntity];
    if (!cfg?.apiPath || !item.id) return;
    setConfirmState({
      title: `Delete this ${cfg.label}?`,
      description: `${item.title || item.name || item.slug || `#${item.id}`} will be removed from WordPress. This cannot be undone.`,
      confirmLabel: `Yes, delete ${cfg.label}`,
      destructive: true,
      onConfirm: async () => {
        try {
          await bridgeApi.del(cfg.apiPath, orgId, item.id);
          await fetchEntity(activeEntity);
          setViewMode('list');
          setSelectedItem(null);
        } catch (e) {
          alert(`Delete failed: ${e.response?.data?.detail || e.message}`);
        }
      },
    });
  };

  // Move a position up or down within its staff group. Uses optimistic local
  // state then PATCHes the swapped pair's display_order on WP.
  const handleReorder = async (row, direction /* 'up' | 'down' */) => {
    const cfg = ENTITIES[activeEntity];
    if (!cfg?.supportsReorder || !cfg.apiPath) return;
    const sameGroup = (entityData[activeEntity] || []).filter(r => r.staff_group === row.staff_group);
    sameGroup.sort((a, b) => (a.display_order ?? 999) - (b.display_order ?? 999));
    const idx = sameGroup.findIndex(r => r.id === row.id);
    if (idx < 0) return;
    const targetIdx = direction === 'up' ? idx - 1 : idx + 1;
    if (targetIdx < 0 || targetIdx >= sameGroup.length) return;
    const a = sameGroup[idx], b = sameGroup[targetIdx];
    const aOrder = a.display_order ?? (idx + 1);
    const bOrder = b.display_order ?? (targetIdx + 1);
    try {
      await Promise.all([
        bridgeApi.patch(cfg.apiPath, orgId, a.id, { display_order: bOrder }),
        bridgeApi.patch(cfg.apiPath, orgId, b.id, { display_order: aOrder }),
      ]);
      await fetchEntity(activeEntity);
    } catch (e) {
      alert(`Reorder failed: ${e.response?.data?.detail || e.message}`);
    }
  };

  if (configLoading) {
    return <div className="flex items-center justify-center py-16"><Loader2 className="h-8 w-8 animate-spin text-amber-500" /></div>;
  }

  if (!isLive) {
    return (
      <>
        <ConnectionPanel
          orgId={orgId} siteConfig={siteConfig} setSiteConfig={setSiteConfig}
          onConnected={() => window.location.reload()}
          onDeleteRequest={setConfirmState}
        />
        <ConfirmDialog state={confirmState} onClose={() => setConfirmState(null)} />
      </>
    );
  }

  const health = siteConfig?.health_data;

  // ── Conflict Resolution Modal ──
  const conflictModal = conflict && (
    <div className="fixed inset-0 z-50 bg-black/60 flex items-center justify-center p-4" data-testid="conflict-modal">
      <Card className="bg-slate-800 border-slate-600 max-w-2xl w-full max-h-[80vh] overflow-y-auto">
        <CardContent className="p-6 space-y-4">
          <div className="flex items-center gap-2 text-amber-400">
            <AlertTriangle className="h-5 w-5" />
            <h3 className="font-bold text-lg">Save Conflict</h3>
          </div>
          <p className="text-sm text-slate-300">This record was modified by another user while you were editing. Choose how to resolve:</p>
          {conflict.remote && (
            <div className="space-y-2">
              <h4 className="text-xs font-bold text-slate-400 uppercase">Changed Fields</h4>
              <div className="bg-slate-900 rounded p-3 text-xs font-mono space-y-1 max-h-48 overflow-y-auto">
                {Object.keys(conflict.local).filter(k => {
                  const lv = conflict.local[k];
                  const rv = conflict.remote?.[k];
                  return rv !== undefined && JSON.stringify(lv) !== JSON.stringify(rv) && !k.startsWith('_');
                }).map(k => (
                  <div key={k} className="flex gap-2">
                    <span className="text-slate-500 w-32 shrink-0">{k}:</span>
                    <span className="text-red-400 line-through">{JSON.stringify(conflict.remote[k])}</span>
                    <span className="text-emerald-400">{JSON.stringify(conflict.local[k])}</span>
                  </div>
                ))}
              </div>
            </div>
          )}
          <div className="flex gap-2 justify-end pt-2">
            <Button variant="outline" className="border-slate-600 text-slate-300" onClick={() => setConflict(null)}>Cancel</Button>
            <Button variant="outline" className="border-blue-600 text-blue-400" onClick={async () => {
              // Force save with updated revision
              const merged = { ...conflict.local };
              if (conflict.remote?._ol_revision_id) merged._ol_revision_id = conflict.remote._ol_revision_id;
              setConflict(null);
              await handleSave(merged);
            }}>Force Save (My Changes)</Button>
            <Button className="bg-amber-600 hover:bg-amber-700" onClick={() => {
              setConflict(null);
              setSelectedItem(conflict.remote);
              setViewMode('edit');
            }}>Load Server Version</Button>
          </div>
        </CardContent>
      </Card>
    </div>
  );

  return (
    <div className="space-y-3" data-testid="admin-manager">
      {conflictModal}
      <ConfirmDialog state={confirmState} onClose={() => setConfirmState(null)} />
      <SiteEnvBanner siteConfig={siteConfig} />
      {/* Entity Navigation Bar — wraps onto a second row instead of
          horizontally scrolling. Hidden scrollbars on tab strips are an
          accessibility/discoverability anti-pattern (AJ rightly flagged
          this). With flex-wrap every entity is always visible regardless
          of viewport width; the row simply gets taller when the tabs
          can't fit on one line. */}
      <div className="flex flex-wrap items-center gap-1.5 pb-1" data-testid="entity-nav">
        {ENTITY_NAV_ORDER.map(key => {
          const cfg = ENTITIES[key];
          if (!cfg) return null;
          const Icon = ICONS[cfg.icon];
          const isActive = activeEntity === key;
          return (
            <button
              key={key}
              onClick={() => navigateTo(key)}
              title={cfg.description || cfg.tooltip || ''}
              className={`flex items-center gap-1.5 px-3 py-1.5 rounded-md text-xs font-medium whitespace-nowrap transition-colors ${isActive ? 'bg-amber-600 text-white' : 'bg-slate-700/60 text-slate-200 hover:bg-slate-600/70 hover:text-white border border-slate-600/50'}`}
              data-testid={`nav-${key}`}
            >
              {Icon && <Icon className="h-3.5 w-3.5" />}
              {cfg.plural || cfg.label}
            </button>
          );
        })}
        <div className="flex-1" />
        <button
          onClick={() => setShowImport(true)}
          title="Import venues, sports, schedules, teams, players or games from a CSV file or a Google Sheet"
          className="flex items-center gap-1.5 px-3 py-1.5 rounded-md text-xs font-medium whitespace-nowrap bg-emerald-700/70 text-emerald-100 hover:bg-emerald-600 border border-emerald-600/50 transition-colors"
          data-testid="open-importer-btn"
        >
          <Database className="h-3.5 w-3.5" /> Import
        </button>
        {primaryRole !== 'tech_specialist' && <ResetCacheButton orgId={orgId} />}
        <SyncHealthPill orgId={orgId} />
      </div>

      <OlImporter
        open={showImport}
        onOpenChange={setShowImport}
        orgId={orgId}
        defaultEntity={activeEntity}
        onCommitted={(ent) => { fetchEntity(ent); if (ent !== activeEntity) fetchEntity(activeEntity); }}
      />

      {/* Dashboard */}
      {activeEntity === 'dashboard' && (
        <AdminDashboard health={health} siteConfig={siteConfig} orgId={orgId} role={primaryRole} onNavigate={(e, mode) => navigateTo(e, mode || 'list')} />
      )}

      {/* Media Library — bespoke panel (not the generic DataTable) */}
      {activeEntity === 'media' && (
        <MediaLibraryPanel orgId={orgId} />
      )}

      {/* Settings sub-tab intentionally removed in Session 19 (May 12 2026).
          All WP/Bridge configuration lives under Tech Settings → Site Linking
          (Tech Specialist only). This avoids two parallel UIs editing the
          same ol_site_config document. */}

      {/* Entity List View */}
      {activeEntity !== 'dashboard' && activeEntity !== 'media' && viewMode === 'list' && ENTITIES[activeEntity] && (
        <DataTable
          data={(() => {
            const search = (searchTerms[activeEntity] || '').toLowerCase();
            const titleField = ENTITIES[activeEntity].titleField || 'title';
            let rows = (entityData[activeEntity] || []).filter(r => {
              const matchesSearch = !search ||
                (r[titleField] || r.title || r.name || '').toLowerCase().includes(search) ||
                (r.slug || '').toLowerCase().includes(search);
              // Staff Positions Level filter (client-side — the full positions
              // dataset loads in one page, so this scopes the whole set).
              if (activeEntity === 'positions' && positionsFilter.level !== 'all') {
                if ((r.level || '').trim().toLowerCase() !== positionsFilter.level) return false;
              }
              // Staff Groups Level filter (client-side — full dataset loads).
              if (activeEntity === 'staff-groups' && staffGroupsFilter.level !== 'all') {
                if ((r.level_gender || '').trim().toLowerCase() !== staffGroupsFilter.level) return false;
              }
              return matchesSearch;
              // Games season/team/sport inline filters are applied server-side
              // via fetchEntity() so the filter scope is the full dataset
              // (not just the loaded 200 rows). The client-side join through
              // schedules previously used here was both broken (looked at the
              // wrong field name `season_slug` instead of `season`) and
              // pagination-blind. Don't restore it.
            });
            const sc = sortConfigs[activeEntity];
            if (sc?.key) {
              const dir = sc.dir === 'desc' ? -1 : 1;
              rows = rows.slice().sort((a, b) => {
                const av = a[sc.key];
                const bv = b[sc.key];
                if (av === bv) return 0;
                if (av === null || av === undefined || av === '') return 1 * dir;
                if (bv === null || bv === undefined || bv === '') return -1 * dir;
                if (typeof av === 'number' && typeof bv === 'number') return (av - bv) * dir;
                return String(av).localeCompare(String(bv), undefined, { numeric: true, sensitivity: 'base' }) * dir;
              });
            }
            return rows;
          })()}
          columns={(() => {
            const base = ENTITIES[activeEntity].columns || [];
            // For positions, prepend a tiny reorder-controls column.
            if (ENTITIES[activeEntity].supportsReorder) {
              const same = (row) => (entityData[activeEntity] || [])
                .filter(r => r.staff_group === row.staff_group)
                .sort((a, b) => (a.display_order ?? 999) - (b.display_order ?? 999));
              const ctrl = {
                key: '_reorder', label: '↕', tooltip: 'Reorder within this staff group',
                render: (r) => {
                  const list = same(r);
                  const idx = list.findIndex(x => x.id === r.id);
                  const isFirst = idx === 0;
                  const isLast = idx === list.length - 1;
                  return (
                    <span className="inline-flex items-center gap-0.5">
                      <button
                        type="button"
                        disabled={isFirst}
                        onClick={(e) => { e.stopPropagation(); handleReorder(r, 'up'); }}
                        className={`h-5 w-5 rounded text-[10px] ${isFirst ? 'text-slate-600 cursor-not-allowed' : 'text-amber-300 hover:bg-amber-900/30'}`}
                        title="Move up"
                        data-testid={`reorder-up-${r.id}`}
                      ><ArrowUp className="h-3 w-3" /></button>
                      <button
                        type="button"
                        disabled={isLast}
                        onClick={(e) => { e.stopPropagation(); handleReorder(r, 'down'); }}
                        className={`h-5 w-5 rounded text-[10px] ${isLast ? 'text-slate-600 cursor-not-allowed' : 'text-amber-300 hover:bg-amber-900/30'}`}
                        title="Move down"
                        data-testid={`reorder-down-${r.id}`}
                      ><ArrowDown className="h-3 w-3" /></button>
                    </span>
                  );
                },
              };
              return [ctrl, ...base];
            }
            // Games grid 'Website' (W) column — ON mode only. Bound to the
            // derived is_published; the toggle PATCHes {published} through the
            // SAME path as the Scheduler W (migration_log_0169).
            if (activeEntity === 'games' && publishFlagOn) {
              return [...base, {
                key: 'published', displayKey: 'is_published', label: 'Website',
                type: 'boolean', yesLabel: 'On', noLabel: 'Hidden',
                tooltip: 'On website (W) — same toggle as the Scheduler W pill',
              }];
            }
            return base;
          })()}
          loading={entityLoading[activeEntity]}
          searchTerm={searchTerms[activeEntity] || ''}
          onSearchChange={v => setSearchTerms(prev => ({ ...prev, [activeEntity]: v }))}
          page={pages[activeEntity] || 1}
          totalPages={entityMeta[activeEntity]?.total_pages
            || Math.max(1, Math.ceil((entityMeta[activeEntity]?.total || (entityData[activeEntity] || []).length) / 200))}
          onPageChange={p => { setPages(prev => ({ ...prev, [activeEntity]: p })); fetchEntity(activeEntity, { page: p }); }}
          onRowEdit={row => navigateTo(activeEntity, 'edit', row)}
          archiveReadOnly={!!ENTITIES[activeEntity].archiveRowsReadOnly}
          onRowPull={PULLABLE_ENTITIES.has(activeEntity) ? handleRowPull : null}
          onRowDelete={ENTITIES[activeEntity].readOnly ? null : handleDelete}
          onBulkDelete={ENTITIES[activeEntity].readOnly ? null : handleBulkDelete}
          onBulkPull={PULLABLE_ENTITIES.has(activeEntity) ? handleBulkPull : null}
          onBulkSave={ENTITIES[activeEntity].readOnly ? null : handleBulkSave}
          sortKey={sortConfigs[activeEntity]?.key}
          sortDir={sortConfigs[activeEntity]?.dir}
          onSort={(key) => setSortConfigs(prev => {
            const cur = prev[activeEntity] || {};
            const dir = cur.key === key && cur.dir === 'asc' ? 'desc' : 'asc';
            return { ...prev, [activeEntity]: { key, dir } };
          })}
          onCellSave={ENTITIES[activeEntity].readOnly ? null : handleCellSave}
          onNew={(ENTITIES[activeEntity].readOnly && !ENTITIES[activeEntity].allowNew) ? null : () => navigateTo(activeEntity, 'new')}
          entityLabel={ENTITIES[activeEntity].label}
          entities={entityData}
          siteTimezone={siteConfig?.settings?.timezone || 'America/New_York'}
          // Inline filters rendered between search + New Game for the Games tab.
          inlineFilters={activeEntity === 'games' ? (
            <>
              <select
                value={gamesFilter.season}
                onChange={e => setGamesFilter(p => ({ ...p, season: e.target.value }))}
                className="h-9 text-sm rounded-md px-2 bg-slate-800 border border-slate-600 text-slate-100"
                data-testid="games-filter-season"
                title="Filter by season"
              >
                <option value="" disabled>Select season…</option>
                {seasonOptionsFor(gamesFilter.sport).map(s => (
                  <option key={s.id || s.slug} value={s.slug}>{s.title || s.name || s.slug}</option>
                ))}
              </select>
              <select
                value={gamesFilter.sport}
                onChange={e => { gamesSportTouchedRef.current = true; setGamesFilter(p => ({ ...p, sport: e.target.value })); }}
                className="h-9 text-sm rounded-md px-2 bg-slate-800 border border-slate-600 text-slate-100"
                data-testid="games-filter-sport"
                title="Filter by sport"
              >
                <option value="all">All sports</option>
                {(entityData.sports || []).map(s => (
                  <option key={s.id || s.slug} value={s.slug || s.id}>{s.title || s.name || s.slug}</option>
                ))}
              </select>
              <select
                value={gamesFilter.team}
                onChange={e => setGamesFilter(p => ({ ...p, team: e.target.value }))}
                className="h-9 text-sm rounded-md px-2 bg-slate-800 border border-slate-600 text-slate-100"
                data-testid="games-filter-team"
                title="Filter by our team (SWS internal — not opponents)"
              >
                <option value="all">All our teams</option>
                {dedupedSwsTeams.map(t => (
                  <option key={t.id} value={String(t.id)}>{t.title || t.name || t.slug}</option>
                ))}
              </select>
              <label
                className="flex items-center gap-2 h-9 px-3 rounded-md bg-slate-800 border border-slate-600 text-sm text-slate-200 cursor-pointer select-none"
                data-testid="games-filter-hide-events"
                title="Hide pep rallies, banquets, tryouts and other non-game events — show only real matchups"
              >
                <input
                  type="checkbox"
                  checked={gamesFilter.hideEvents}
                  onChange={e => setGamesFilter(p => ({ ...p, hideEvents: e.target.checked }))}
                  className="accent-sky-500 h-4 w-4"
                  data-testid="games-filter-hide-events-checkbox"
                />
                Matchups only
              </label>
              {publishFlagOn && (
                <select
                  value={gamesFilter.show}
                  onChange={e => setGamesFilter(p => ({ ...p, show: e.target.value }))}
                  className="h-9 text-sm rounded-md px-2 bg-slate-800 border border-slate-600 text-slate-100"
                  data-testid="games-filter-show"
                  title="Filter by website visibility"
                >
                  <option value="all">Show: All</option>
                  <option value="on">On website</option>
                  <option value="hidden">Hidden</option>
                </select>
              )}
            </>
          ) : activeEntity === 'schedules' ? (
            <select
              value={schedulesFilter.season}
              onChange={e => setSchedulesFilter({ season: e.target.value })}
              className="h-9 text-sm rounded-md px-2 bg-slate-800 border border-slate-600 text-slate-100"
              data-testid="schedules-filter-season"
              title="Filter by season"
            >
              <option value="all">All seasons</option>
              {seasonOptionsFor('all').map(s => (
                <option key={s.id || s.slug} value={s.slug}>{s.title || s.name || s.slug}</option>
              ))}
            </select>
          ) : activeEntity === 'rosters' ? (
            <select
              value={rostersFilter.season}
              onChange={e => setRostersFilter({ season: e.target.value })}
              className="h-9 text-sm rounded-md px-2 bg-slate-800 border border-slate-600 text-slate-100"
              data-testid="rosters-filter-season"
              title="Filter by season"
            >
              <option value="current">Current + next season</option>
              <option value="all">All seasons (incl. past)</option>
              {(entityData.seasons || []).map(s => (
                <option key={s.id || s.slug} value={s.slug}>{s.title || s.name || s.slug}</option>
              ))}
            </select>
          ) : activeEntity === 'players' ? (
            <>
              <select
                value={playersFilter.sport}
                onChange={e => setPlayersFilter(p => ({ ...p, sport: e.target.value }))}
                className="h-9 text-sm rounded-md px-2 bg-slate-800 border border-slate-600 text-slate-100"
                data-testid="players-filter-sport"
                title="Filter by sport (from roster assignment)"
              >
                <option value="all">All sports</option>
                {(entityData.sports || []).map(s => (
                  <option key={s.id || s.slug} value={s.slug || s.id}>{s.title || s.name || s.slug}</option>
                ))}
              </select>
              <select
                value={playersFilter.season}
                onChange={e => setPlayersFilter(p => ({ ...p, season: e.target.value, roster: 'all' }))}
                className="h-9 text-sm rounded-md px-2 bg-slate-800 border border-slate-600 text-slate-100"
                data-testid="players-filter-season"
                title="Filter by season (from roster assignment)"
              >
                <option value="all">All seasons</option>
                {seasonOptionsFor(playersFilter.sport).map(s => (
                  <option key={s.id || s.slug} value={s.slug}>{s.title || s.name || s.slug}</option>
                ))}
              </select>
              <select
                value={playersFilter.roster}
                onChange={e => setPlayersFilter(p => ({ ...p, roster: e.target.value }))}
                className="h-9 text-sm rounded-md px-2 bg-slate-800 border border-slate-600 text-slate-100"
                data-testid="players-filter-roster"
                title="Filter by roster team (scoped to the chosen sport + season)"
              >
                <option value="all">All teams</option>
                {(entityData.rosters || [])
                  .filter(r => r.slug
                    && (playersFilter.sport === 'all' || (r.slug || '').split('-')[0] === playersFilter.sport)
                    && (!playersFilter.season || playersFilter.season === 'all' || r.season === playersFilter.season))
                  .sort((a, b) => (a.name || a.slug).localeCompare(b.name || b.slug))
                  .map(r => (
                    <option key={r.slug} value={r.slug}>{r.name || r.slug}</option>
                  ))}
              </select>
            </>
          ) : activeEntity === 'coaches' ? (
            <>
              <select
                value={staffFilter.sport}
                onChange={e => setStaffFilter(p => ({ ...p, sport: e.target.value, team: 'all' }))}
                className="h-9 text-sm rounded-md px-2 bg-slate-800 border border-slate-600 text-slate-100"
                data-testid="staff-filter-sport"
                title="Filter by sport (from staff-group assignment)"
              >
                <option value="all">All sports</option>
                {(entityData.sports || []).map(s => (
                  <option key={s.id || s.slug} value={s.slug || s.id}>{s.title || s.name || s.slug}</option>
                ))}
              </select>
              <select
                value={staffFilter.team}
                onChange={e => setStaffFilter(p => ({ ...p, team: e.target.value }))}
                className="h-9 text-sm rounded-md px-2 bg-slate-800 border border-slate-600 text-slate-100"
                data-testid="staff-filter-team"
                title="Filter by staff group / team"
              >
                <option value="all">All groups</option>
                {(entityData['staff-groups'] || [])
                  .filter(g => staffFilter.sport === 'all' || (g.slug || '').startsWith(staffFilter.sport))
                  .map(g => (
                    <option key={g.id || g.slug} value={g.slug}>{g.name || g.title || g.slug}</option>
                  ))}
              </select>
              <select
                value={staffFilter.season}
                onChange={e => setStaffFilter(p => ({ ...p, season: e.target.value }))}
                className="h-9 text-sm rounded-md px-2 bg-slate-800 border border-slate-600 text-slate-100"
                data-testid="staff-filter-season"
                title="Filter by season (current staff vs production-historical staff)"
              >
                <option value="all">All seasons</option>
                {seasonOptionsFor(staffFilter.sport).map(s => (
                  <option key={s.id || s.slug} value={s.slug}>{s.title || s.name || s.slug}</option>
                ))}
              </select>
            </>
          ) : activeEntity === 'positions' ? (
            <>
              <select
                value={positionsFilter.sport}
                onChange={e => setPositionsFilter(p => ({ ...p, sport: e.target.value, level: 'all' }))}
                className="h-9 text-sm rounded-md px-2 bg-slate-800 border border-slate-600 text-slate-100"
                data-testid="positions-filter-sport"
                title="Filter staff positions by sport, or show only organization / leadership roles"
              >
                <option value="all">All sports</option>
                {(entityData.sports || []).map(s => (
                  <option key={s.id || s.slug} value={s.slug || s.id}>{s.title || s.name || s.slug}</option>
                ))}
                <option value="org">Organization (leadership)</option>
              </select>
              <select
                value={positionsFilter.level}
                onChange={e => setPositionsFilter(p => ({ ...p, level: e.target.value }))}
                className="h-9 text-sm rounded-md px-2 bg-slate-800 border border-slate-600 text-slate-100"
                data-testid="positions-filter-level"
                title="Filter staff positions by level (only levels present in the data are listed)"
              >
                <option value="all">All levels</option>
                {positionLevelOptions.map(o => (
                  <option key={o.value} value={o.value}>{o.label}</option>
                ))}
              </select>
            </>
          ) : activeEntity === 'staff-groups' ? (
            <select
              value={staffGroupsFilter.level}
              onChange={e => setStaffGroupsFilter(p => ({ ...p, level: e.target.value }))}
              className="h-9 text-sm rounded-md px-2 bg-slate-800 border border-slate-600 text-slate-100"
              data-testid="staff-groups-filter-level"
              title="Filter staff groups by level (only levels present in the data are listed)"
            >
              <option value="all">All levels</option>
              {staffGroupLevelOptions.map(o => (
                <option key={o.value} value={o.value}>{o.label}</option>
              ))}
            </select>
          ) : (ENTITIES[activeEntity].headerActions || []).length > 0 ? (
            <>
              {ENTITIES[activeEntity].headerActions.map(action => (
                <Button
                  key={action.key}
                  size="sm" variant="outline"
                  onClick={() => handleHeaderAction(action)}
                  className="border-amber-700/60 text-amber-300 hover:bg-amber-900/30 h-8 text-[11px]"
                  data-testid={`header-action-${action.key}`}
                  title={action.confirmDescription}
                >
                  {action.label}
                </Button>
              ))}
            </>
          ) : null}
        />
      )}

      {/* Entity Edit/New View */}
      {activeEntity !== 'dashboard' && activeEntity !== 'media' && (viewMode === 'edit' || viewMode === 'new') && ENTITIES[activeEntity] && (
        <EntityForm
          entityConfig={(() => {
            const cfg = ENTITIES[activeEntity];
            if (activeEntity === 'games' && publishFlagOn) {
              const sections = (cfg.formSections || []).map((s, i) => i === 0
                ? { ...s, fields: [...s.fields, {
                    key: 'published', label: 'Show on website', type: 'checkbox',
                    help: 'When on, this game appears on the public website (same as the Scheduler W pill). New games default to on.' }] }
                : s);
              return { ...cfg, formSections: sections };
            }
            return cfg;
          })()}
          initialValues={(() => {
            const tz = siteConfig?.settings?.timezone || 'America/New_York';
            const base = viewMode === 'edit'
              ? { ...selectedItem, _siteTimezone: tz }
              : { _siteTimezone: tz };
            if (activeEntity === 'games' && publishFlagOn && viewMode !== 'edit' && base.published === undefined) {
              base.published = true;
            }
            return base;
          })()}
          onSave={handleSave}
          onDelete={ENTITIES[activeEntity].readOnly ? null : handleDelete}
          onCancel={() => { setViewMode('list'); setSelectedItem(null); }}
          saving={saving}
          entities={{
            ...entityData,
            teams: pickerTeams.length ? pickerTeams : entityData.teams,
            venues: pickerVenues.length ? pickerVenues : entityData.venues,
          }}
          entityKey={activeEntity}
          orgId={orgId}
          onNavigate={navigateTo}
        />
      )}

      {/* Schedule Builder */}
      {viewMode === 'builder' && (
        <ScheduleBuilder
          orgId={orgId}
          schedules={entityData.schedules || []}
          teams={entityData.teams || []}
          venues={entityData.venues || []}
          onBack={() => { setViewMode('list'); setActiveEntity('games'); }}
          onComplete={() => { fetchEntity('games'); setViewMode('list'); setActiveEntity('games'); }}
        />
      )}
    </div>
  );
}
